/**
 * Bug fix (AB#35119): extracts the server's own validation message(s) out of an ASP.NET
 * ProblemDetails response so Quick Entry can show WHY a save was refused, instead of a generic
 * "please try again".
 *
 * Concretely: creating a requestor contact whose email domain is on
 * `dbo.tblRequestorEmailDomainBlackList` is correctly rejected by the server. `RequestorController`
 * carries `[ApiController]`, so `NewSendContact`'s `IValidatableObject.Validate` (which calls
 * `IContactValidator.IsDomainBlacklisted`) produces an automatic **400** with body:
 *
 *   { "errors": { "Email": ["Email domain 'nefarious.com' is not allowed. Email must be removed
 *     or corrected before contact information can be saved."] }, "status": 400, ... }
 *
 * Quick Entry swallowed that in a bare `catch` and toasted "Couldn't save the contact - please try
 * again, or set it in Classic Mode." Retrying can never succeed for a validation failure, so the
 * specialist reasonably concluded the value had saved.
 *
 * Only surfaces messages for a genuine 400 validation payload; any other failure (500, network
 * drop) falls back to the caller's generic message, so a transient error still reads as retryable.
 */

/**
 * AB#35119 (reopened): there are TWO 400 shapes in this API, and the reported case is the second one.
 *
 * `[ApiController]` model validation produces ProblemDetails (`{ errors: { Email: [...] } }`) - that is
 * the create-contact path this helper was first written for. But
 * `RequestorController.UpdateSendToRequestorContact` - the endpoint Classic's auto-save uses, and the
 * one Quick Entry now calls - reports the same blacklist rule with `throw new Exception(...)`, which
 * `GetError(CustomError)` turns into the LEGACY envelope: `{ code: 999, message: "Email domain
 * 'nefarious.com' is not allowed…" }`, or an array of those. That is exactly what the full-page error
 * banner renders as "API Error Code: 999 - …" (see APIErrorPopUp).
 *
 * Parsing only ProblemDetails would have swallowed the message again on the very path this bug is
 * about, so both shapes are handled here.
 */
/** One entry of the legacy custom-error envelope, as APIErrorPopUp renders it. */
interface LegacyErrorEntry {
    readonly code?: number | string;
    readonly message?: string;
}

/** ASP.NET ProblemDetails body, plus the single-object form of the legacy envelope. */
interface ProblemDetailsBody extends LegacyErrorEntry {
    readonly errors?: Record<string, readonly string[] | string>;
    readonly title?: string;
    readonly detail?: string;
}

interface ProblemDetailsError {
    readonly response?: {
        readonly status?: number;
        readonly data?: ProblemDetailsBody | ReadonlyArray<LegacyErrorEntry>;
    };
}

/**
 * Returns a human-readable validation message from a 400 ProblemDetails response, or `undefined`
 * when the error isn't a validation failure (so the caller keeps its own generic wording).
 *
 * Multiple field errors are joined so nothing is silently dropped. Field NAMES are deliberately
 * omitted from the output: the server's messages already read as complete sentences, and the raw
 * names are contract identifiers ("Email", "SendToRequestor") rather than the labels the
 * specialist sees on screen.
 */
export function serverValidationMessage(error: unknown): string | undefined {
    const problem = error as ProblemDetailsError | undefined;
    const response = problem?.response;
    if (!response || response.status !== 400) {
        return undefined;
    }
    // Legacy custom-error envelope, either a single object or an array of them (the two forms
    // APIErrorPopUp itself handles). Checked BEFORE ProblemDetails because this shape has no
    // `errors` key at all, so the ProblemDetails path below would fall through to `detail` and
    // return undefined - swallowing the message on exactly the endpoint this bug is about.
    const raw = response.data;
    if (Array.isArray(raw)) {
        const joined = (raw as ReadonlyArray<LegacyErrorEntry>)
            .map(entry => entry?.message?.trim())
            .filter((message): message is string => Boolean(message))
            .join(" ");
        return joined.length > 0 ? joined : undefined;
    }
    const data = raw as ProblemDetailsBody | undefined;
    if (!data?.errors) {
        const legacy = data?.message?.trim();
        if (legacy) {
            return legacy;
        }
    }
    const errors = data?.errors;
    if (!errors) {
        // A 400 with no per-field errors: fall back to the top-level detail/title if it is
        // something more specific than ASP.NET's boilerplate validation title.
        const detail = data?.detail?.trim();
        return detail && detail.length > 0 ? detail : undefined;
    }
    const messages: string[] = [];
    for (const value of Object.values(errors)) {
        if (typeof value === "string") {
            if (value.trim()) {
                messages.push(value.trim());
            }
            continue;
        }
        for (const message of value ?? []) {
            if (typeof message === "string" && message.trim()) {
                messages.push(message.trim());
            }
        }
    }
    return messages.length > 0 ? messages.join(" ") : undefined;
}
